import { useCallback, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

const MAX_HINT = 140;

export function truncateHint(text: string, max = MAX_HINT): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trim()}…`;
}

interface TipPosition {
  top: number;
  left: number;
  placement: "top" | "bottom";
}

function computePosition(rect: DOMRect): TipPosition {
  const margin = 8;
  const tipH = 72;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spaceBelow = vh - rect.bottom;
  const placement = spaceBelow >= tipH + margin ? "bottom" : "top";
  const centerX = rect.left + rect.width / 2;
  const left = Math.min(Math.max(centerX, 12 + 140), vw - 12 - 140);
  const top =
    placement === "bottom" ? rect.bottom + margin : rect.top - margin;
  return { top, left, placement };
}

interface CatalogHoverTipProps {
  text: string;
  label: string;
  children: ReactNode;
}

/** Infobulle brève au survol — rendue en portal pour ne pas être coupée par la grille scrollable. */
export function CatalogHoverTip({ text, label, children }: CatalogHoverTipProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<TipPosition | null>(null);
  const hint = truncateHint(text);

  const show = useCallback(() => {
    if (!hint || !wrapRef.current) return;
    setPos(computePosition(wrapRef.current.getBoundingClientRect()));
    setVisible(true);
  }, [hint]);

  const hide = useCallback(() => setVisible(false), []);

  return (
    <>
      <div
        ref={wrapRef}
        className="pep-trial-chip-hover-wrap"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </div>
      {visible &&
        hint &&
        pos &&
        createPortal(
          <div
            className={`pep-catalog-hover-tip pep-catalog-hover-tip--${pos.placement}`}
            style={{
              top: pos.placement === "bottom" ? pos.top : undefined,
              bottom: pos.placement === "top" ? window.innerHeight - pos.top : undefined,
              left: pos.left,
            }}
            role="tooltip"
            aria-hidden
          >
            <span className="pep-catalog-hover-tip__title">{label}</span>
            <span className="pep-catalog-hover-tip__text">{hint}</span>
          </div>,
          document.body,
        )}
    </>
  );
}
